import { ChangeEvent } from 'react';
import { Autocomplete, CircularProgress, MenuItem } from '@mui/material';
import { Currency } from '../../../../../../pages/dashboard/payments/interfaces/Currency';
import { formatDecimalNumber } from '../../../../../../utils/helper';
import Customdatepicker from '../../../../../customdatepicker/customdatepicker';
import CustomTextField from '../../../../../CustomTextInput';
import Signature from '../../../../../signature/signature';
import {
    PurchaseOrder,
    PurchaseOrderFormErrors
} from '../../../../../../interfaces/PurchaseOrder';

interface PurchaseOrderStepTwoFormProps {
    updateField: (name: string, value: string | number) => void;
    payload: PurchaseOrder;
    errors: PurchaseOrderFormErrors;
    currencies: Currency[];
    loadingCurrencies: boolean;
    setSignatureUrlBase64: (value: string) => void;
    signatureUrlBase64: string;
    totalAmount: number;
    onSaveOnlyAction?: () => void;
    showPreview: boolean;
    setShowPreview: (val: boolean) => void;
}

const StepTwo = ({
    updateField,
    payload,
    errors,
    currencies,
    loadingCurrencies,
    setSignatureUrlBase64,
    signatureUrlBase64,
    totalAmount
}: PurchaseOrderStepTwoFormProps) => {
    return (
        <div className='w-full flex flex-col gap-4'>
            <Autocomplete
                value={payload.currencyCode as unknown as Currency}
                placeholder='Currency'
                disableClearable
                id='send-currency'
                sx={{
                    marginTop: '1rem',
                    '.MuiInputBase-input': {
                        height: '0.3rem'
                    }
                }}
                renderInput={(params) => (
                    <CustomTextField
                        params={params}
                        value={payload.currencyCodeValue}
                        label='Select Currency'
                        placeholder='Currency'
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: loadingCurrencies && (
                                <>
                                    <CircularProgress size={20} />
                                    {params.InputProps.endAdornment}
                                </>
                            )
                        }}
                    />
                )}
                noOptionsText={'No data available'}
                renderOption={(props, option) => (
                    <MenuItem
                        {...props}
                        value={option.name}
                        key={`countries-${option.id}`}
                    >
                        {option.name}
                    </MenuItem>
                )}
                getOptionLabel={(option) => option.nameAndFlag}
                onChange={(_, value: Currency) => {
                    if (value !== null) {
                        updateField('currencyCode', value.currencyCode);
                        updateField(
                            'currencyCodeValue',
                            value as unknown as string
                        );
                    }
                }}
                options={currencies}
            />

            <CustomTextField
                disabled
                id='purchaseorder-totalAmount'
                label='Total Amount'
                name='totalAmount'
                value={formatDecimalNumber(totalAmount)}
            />

            <CustomTextField
                multiline
                rows={7.3}
                height={156}
                id='purchaseorder-additionalInformation'
                label='Additional Information'
                name='additionalInformation'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('additionalInformation', e.target.value);
                }}
            />

            <CustomTextField
                error={errors?.placeOfIssue.length > 0}
                helperText={
                    errors?.placeOfIssue.length > 0
                        ? errors?.placeOfIssue
                        : undefined
                }
                required
                id='quotation-placeOfIssue'
                label='Place of Issue'
                name='placeOfIssue'
                value={payload?.placeOfIssue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('placeOfIssue', e.target.value);
                }}
            />

            <Customdatepicker
                id='purchaseorder-issuedDate'
                label='Date of Issue'
                name='issuedDate'
                updateField={updateField}
                error={errors?.issuedDate.length > 0}
                helperText={
                    errors?.issuedDate.length > 0
                        ? errors?.issuedDate
                        : undefined
                }
            />

            <CustomTextField
                error={errors?.signatoryCompany.length > 0}
                helperText={
                    errors?.signatoryCompany.length > 0
                        ? errors?.signatoryCompany
                        : undefined
                }
                required
                id='proformainvoice-signatoryCompany'
                label='Signatory Company'
                name='signatoryCompany'
                value={payload?.signatoryCompany}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('signatoryCompany', e.target.value);
                }}
            />

            <CustomTextField
                error={errors?.nameOfAuthorisedSignatory.length > 0}
                helperText={
                    errors?.nameOfAuthorisedSignatory.length > 0
                        ? errors?.nameOfAuthorisedSignatory
                        : undefined
                }
                required
                id='proformainvoice-nameOfAuthorisedSignatory'
                label='Name of Authorized Signature'
                name='nameOfAuthorisedSignatory'
                value={payload?.nameOfAuthorisedSignatory}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('nameOfAuthorisedSignatory', e.target.value);
                }}
            />

            <Signature
                name='signatureUrl'
                updateField={updateField}
                setSignatureUrlBase64={setSignatureUrlBase64}
                signatureUrlBase64={signatureUrlBase64}
            />
        </div>
    );
};

export default StepTwo;
